import { useEffect, useId } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { countUnread } from '@/lib/api/notifications';
import { useAuth } from '@/lib/hooks/useAuth';
import { supabase } from '@/lib/supabase/client';
import { cn } from '@/lib/utils/cn';

export function NotificationBell() {
  const { profile } = useAuth();
  const qc = useQueryClient();
  // AppHeader renders this component in both the mobile and desktop rows
  // (toggled by CSS, both present in the DOM). A unique channel name per
  // mount prevents the two instances from colliding on the same realtime
  // topic, which would otherwise throw "cannot add postgres_changes
  // callbacks after subscribe()".
  const instanceId = useId();

  const { data: count = 0 } = useQuery({
    queryKey: ['notif-unread', profile?.id],
    queryFn: () => countUnread(profile!.id),
    enabled: !!profile,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (!profile) return;
    const channel = supabase
      .channel(`notif-bell:${profile.id}:${instanceId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${profile.id}`,
        },
        () => qc.invalidateQueries({ queryKey: ['notif-unread', profile.id] }),
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [profile, qc, instanceId]);

  if (!profile) return null;

  return (
    <Link
      to="/notifications"
      className="relative inline-flex items-center justify-center rounded-full p-2 text-muted hover:bg-cream-warm transition-colors"
      aria-label={count > 0 ? `${count} unread notifications` : 'Notifications'}
    >
      <Bell className="h-5 w-5" aria-hidden="true" />
      {count > 0 && (
        <span
          className={cn(
            'absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-gold text-white text-[10px] font-bold',
            count > 9 && 'w-5',
          )}
          aria-hidden="true"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
}
